import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/abandoned-checkout',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/abandoned-checkout',
        destPath: '/v1/abandoned-checkout',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_abandoned-checkouts';

/** Retrieves an abandoned checkout. */
export function getAbandonedCheckout(
  payload: object
): RequestOptionsFactory<any> {
  function __getAbandonedCheckout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.GetAbandonedCheckout',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath: '/v1/abandoned-checkout/{abandonedCheckoutId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'abandonedCheckout.createdDate' },
              { path: 'abandonedCheckout.updatedDate' },
              { path: 'abandonedCheckout.checkoutRecoveredDate' },
              { path: 'abandonedCheckout.activities.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAbandonedCheckout;
}

/** Deletes an abandoned checkout. */
export function deleteAbandonedCheckout(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteAbandonedCheckout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'DELETE' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.DeleteAbandonedCheckout',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath: '/v1/abandoned-checkout/{abandonedCheckoutId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAbandonedCheckout;
}

/**
 * Creates a query to retrieve a list of abandoned checkouts.
 *
 * The `queryAbandonedCheckouts()` function builds a query to retrieve a list of abandoned checkouts and returns a `ResultsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ResultsQueryBuilder` functions onto the query. `ResultsQueryBuilder` functions enable you to sort, filter, and control the results `queryAbandonedCheckouts()` returns.
 */
export function queryAbandonedCheckouts(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAbandonedCheckouts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.QueryAbandonedCheckouts',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath: '/v1/abandoned-checkout/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.createdDate' },
              { path: 'results.updatedDate' },
              { path: 'results.checkoutRecoveredDate' },
              { path: 'results.activities.createdDate' },
              { path: 'abandonedCheckouts.createdDate' },
              { path: 'abandonedCheckouts.updatedDate' },
              { path: 'abandonedCheckouts.checkoutRecoveredDate' },
              { path: 'abandonedCheckouts.activities.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryAbandonedCheckouts;
}

/** Retrieves a list of abandoned checkouts, given the provided paging, filtering, search details and sorting. Up to 100 abandoned checkouts can be returned per request. */
export function searchAbandonedCheckouts(
  payload: object
): RequestOptionsFactory<any> {
  function __searchAbandonedCheckouts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.SearchAbandonedCheckouts',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath: '/v1/abandoned-checkout/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'abandonedCheckouts.createdDate' },
              { path: 'abandonedCheckouts.updatedDate' },
              { path: 'abandonedCheckouts.checkoutRecoveredDate' },
              { path: 'abandonedCheckouts.activities.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchAbandonedCheckouts;
}

/** Adds a new abandoned checkout activity. */
export function addAbandonedCheckoutActivity(
  payload: object
): RequestOptionsFactory<any> {
  function __addAbandonedCheckoutActivity({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.AddAbandonedCheckoutActivity',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath: '/v1/abandoned-checkouts/{abandonedCheckoutId}/add-activity',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'abandonedCheckout.createdDate' },
              { path: 'abandonedCheckout.updatedDate' },
              { path: 'abandonedCheckout.checkoutRecoveredDate' },
              { path: 'abandonedCheckout.activities.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addAbandonedCheckoutActivity;
}

/** Redirects an abandoned checkout to its checkout page. */
export function redirectToCheckout(
  payload: object
): RequestOptionsFactory<any> {
  function __redirectToCheckout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.abandoned_checkout',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.abandoned_checkout.api.v1.AbandonedCheckoutService.RedirectToCheckout',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomAbandonedCheckoutApiV1AbandonedCheckoutServiceUrl({
        protoPath:
          '/v1/abandoned-checkout/{abandonedCheckoutId}/redirect-to-checkout',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [{ path: 'body' }],
          },
        ]),
    };

    return metadata;
  }

  return __redirectToCheckout;
}
